import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Divider
} from '@mui/material';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/formatting/formatters';
import type { Organization } from '../../types/common';
import type { OrganizationUpdate } from '../../hooks/api/config/useSystemOrganization';

interface SystemOverviewProps {
  system: Organization;
  onUpdate: (updates: OrganizationUpdate) => Promise<void>;
}

export default function SystemOverview({ system, onUpdate }: SystemOverviewProps) {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: system.name || '',
    description: system.description || '',
    metadata: {
      contact_email: system.metadata?.contact_email || '',
      contact_phone: system.metadata?.contact_phone || '',
      timezone: system.metadata?.timezone || 'Europe/Berlin',
      language: system.metadata?.language || 'de',
      country: system.metadata?.country || 'DE'
    }
  });
  const [isSaving, setIsSaving] = useState(false);

  // Sync formData with system prop when it changes (e.g., after successful save)
  useEffect(() => {
    if (system && !isEditing) {
      setFormData({
        name: system.name || '',
        description: system.description || '',
        metadata: {
          contact_email: system.metadata?.contact_email || '',
          contact_phone: system.metadata?.contact_phone || '',
          timezone: system.metadata?.timezone || 'Europe/Berlin',
          language: system.metadata?.language || 'de',
          country: system.metadata?.country || 'DE'
        }
      });
    }
  }, [system, isEditing]);

  const handleChange = (field: string, value: string) => {
    if (field.startsWith('metadata.')) {
      const child = field.replace('metadata.', '');
      setFormData((prev) => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [child]: value,
        },
      }));
      return;
    }

    if (field === 'name' || field === 'description') {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(formData);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: system.name || '',
      description: system.description || '',
      metadata: {
        contact_email: system.metadata?.contact_email || '',
        contact_phone: system.metadata?.contact_phone || '',
        timezone: system.metadata?.timezone || 'Europe/Berlin',
        language: system.metadata?.language || 'de',
        country: system.metadata?.country || 'DE'
      }
    });
    setIsEditing(false);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{t('system.overview.title')}</Typography>
          {!isEditing && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              variant="outlined"
            >
              {t('common.edit')}
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.fields.name')}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'standard'}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.fields.language')}
              value={formData.metadata.language}
              onChange={(e) => handleChange('metadata.language', e.target.value)}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'standard'}
              select={isEditing}
              slotProps={{ select: { native: true } }}
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </TextField>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('system.fields.description')}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'standard'}
              multiline
              rows={2}
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              {t('system.contact.title')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.fields.contactEmail')}
              value={formData.metadata.contact_email}
              onChange={(e) => handleChange('metadata.contact_email', e.target.value)}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'standard'}
              type="email"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.fields.contactPhone')}
              value={formData.metadata.contact_phone}
              onChange={(e) => handleChange('metadata.contact_phone', e.target.value)}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'standard'}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.fields.timezone')}
              value={formData.metadata.timezone}
              onChange={(e) => handleChange('metadata.timezone', e.target.value)}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'standard'}
              select={isEditing}
              slotProps={{ select: { native: true } }}
            >
              <option value="Europe/Berlin">Europe/Berlin</option>
              <option value="Europe/London">Europe/London</option>
              <option value="Europe/Paris">Europe/Paris</option>
              <option value="America/New_York">America/New_York</option>
              <option value="America/Los_Angeles">America/Los_Angeles</option>
              <option value="Asia/Tokyo">Asia/Tokyo</option>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.fields.country')}
              value={formData.metadata.country}
              onChange={(e) => handleChange('metadata.country', e.target.value)}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'standard'}
            />
          </Grid>
        </Grid>

        {isEditing && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={() => {
                void handleSave();
              }}
              disabled={isSaving}
            >
              {t('common.save')}
            </Button>
          </Box>
        )}

        {/* System Info */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('system.info.systemId')}: {system.id || 'system'}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            {t('system.info.createdAt')}: {formatDate(system.created_at, i18n.language)}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            {t('system.info.updatedAt')}: {formatDate(system.updated_at, i18n.language)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
